import logger from '@/utils/logger';
import { Error } from '@/providers/cbr/cbr.types';
import {
  CBRMemberFavouritesResponse,
  CBRDeleteFavouriteResponse,
  CBRInsertFavouriteResponse,
  FavouritePartnerProperties,
  CBRGetFavouriteForUserResponse,
  AccountBalanceRequest,
  AccountBalanceReturn,
} from './user.types';
import CBRProvider from '../../providers/cbr';
import GlpProvider from '../../providers/glp';
import {
  getUserFavouriteQuery,
  deleteFavouriteQuery,
  insertFavouriteQuery,
  getUserFavouritesQuery,
} from './user.query';
import { getCBRProgramID } from '../opco/opco.shared.types';

const generateCbrUserProviderErrorResponse = (
  errors: Error[] = [],
  hasError = true,
) => {
  logger.error("There were errors trying to fetch user's favourites.", {
    errors,
  });

  return {
    hasError,
    error: 'There was an issue with the favourites provider.',
  };
};

const User = {
  async getFavouritePartners({
    opco,
    membershipId,
  }: FavouritePartnerProperties) {
    const provider = new CBRProvider(opco);
    const response = await provider.makeCBRRequest<CBRMemberFavouritesResponse>(
      {
        query: getUserFavouritesQuery,
        variables: {
          memberId: membershipId,
        },
        headers: { 'CBR-MEMBER-ID': membershipId as string },
      },
    );

    if (response.errors || response.hasError) {
      return generateCbrUserProviderErrorResponse(
        response.errors,
        response.hasError,
      );
    }

    return {
      data:
        response.data.member.find(
          ({ program_member_reference }) =>
            program_member_reference === membershipId,
        ) ?? [],
    };
  },

  async partnerIsUserFavorite({
    opco,
    membershipId,
    mechanicId,
  }: FavouritePartnerProperties): Promise<boolean> {
    const provider = new CBRProvider(opco);
    const response =
      await provider.makeCBRRequest<CBRGetFavouriteForUserResponse>({
        query: getUserFavouriteQuery,
        variables: {
          memberId: membershipId,
          mechanicId,
        },
        headers: { 'CBR-MEMBER-ID': membershipId as string },
      });

    return response.data.favourite.length > 0;
  },

  async addFavouritePartner({
    opco,
    membershipId,
    mechanicId,
  }: FavouritePartnerProperties) {
    const provider = new CBRProvider(opco);
    const response = await provider.makeCBRMutation<CBRInsertFavouriteResponse>(
      {
        query: insertFavouriteQuery,
        variables: {
          memberId: membershipId,
          mechanicId,
          programId: getCBRProgramID[opco.opcoId],
        },
        headers: { 'CBR-MEMBER-ID': membershipId as string },
      },
    );

    if (response.errors || response.hasError) {
      return generateCbrUserProviderErrorResponse(
        response.errors,
        response.hasError,
      );
    }

    const [insertedFavourite] = response.data.insert_member.returning;
    return {
      hasError: false,
      data: insertedFavourite?.favourites?.filter(
        x => x.mechanic_id === mechanicId,
      ),
    };
  },

  async removeFavouritePartner({
    opco,
    membershipId,
    favouriteId,
  }: FavouritePartnerProperties) {
    const provider = new CBRProvider(opco);
    const response = await provider.makeCBRMutation<CBRDeleteFavouriteResponse>(
      {
        query: deleteFavouriteQuery,
        variables: {
          memberId: membershipId,
          favouriteId,
        },
        headers: { 'CBR-MEMBER-ID': membershipId as string },
      },
    );

    if (response.errors || response.hasError) {
      return generateCbrUserProviderErrorResponse(
        response.errors,
        response.hasError,
      );
    }

    const { returning } = response.data.delete_favourite;
    const deletedFavourite = returning.find(
      ({ favourite_id }) => favourite_id === Number(favouriteId),
    );

    if (deletedFavourite) return { data: deletedFavourite, hasError: false };
    return { error: `Favourite with id ${favouriteId} was not deleted.` };
  },

  getAviosBalance: (async <T extends boolean>({
    membershipId,
    programId,
    showHouseHoldBalance,
    getAllBalances,
  }: AccountBalanceRequest<T>) => {
    const provider = new GlpProvider();
    const result = await provider.getAccountDetails(
      membershipId,
      programId,
      showHouseHoldBalance,
      getAllBalances,
    );

    const getBalanceByKey = (key: 'REWARD' | 'TOTAL_REWARD' | 'LOAN') =>
      'accounts' in result
        ? String(
            result.accounts.find(({ accountType }) => accountType === key)
              ?.balance.amount,
          )
        : '-';

    if (getAllBalances)
      return {
        rewardBalance: getBalanceByKey('REWARD'),
        totalRewardBalance: getBalanceByKey('TOTAL_REWARD'),
      };

    return 'accounts' in result
      ? String(result.accounts[0]?.balance.amount)
      : '-';
  }) as AccountBalanceReturn,
};

export default User;
